// v3.20.0 — Carte GPS conversationnelle : "conduis-moi à la cathédrale" →
// destination résolue (landmarks Abidjan puis géocodage Mapbox), pré-remplie,
// l'utilisateur confirme et le GPS (/navigate) démarre.
import { useCallback, useEffect, useState } from "react";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

// Landmarks incontournables (résolution instantanée, zéro réseau).
const LANDMARKS = [
  { re: /cath[ée]drale|saint[- ]paul/i, name: "Cathédrale Saint-Paul (Plateau)", lat: 5.3364, lng: -4.0197 },
  { re: /basilique|yamoussoukro/i, name: "Basilique Notre-Dame de la Paix (Yamoussoukro)", lat: 6.8103, lng: -5.2946 },
  { re: /a[ée]roport|fhb\b|f[ée]lix houphou[ëe]t/i, name: "Aéroport FHB (Port-Bouët)", lat: 5.2614, lng: -3.9263 },
  { re: /stade.*(ebimp[ée]|olympique)|ebimp[ée]/i, name: "Stade Olympique d'Ébimpé", lat: 5.4966, lng: -4.0899 },
  { re: /march[ée] de cocody/i, name: "Marché de Cocody", lat: 5.3505, lng: -3.9997 },
  { re: /march[ée] de treichville/i, name: "Marché de Treichville", lat: 5.3081, lng: -4.0125 },
  { re: /grand[- ]bassam|bassam/i, name: "Grand-Bassam (plage)", lat: 5.1958, lng: -3.7382 },
  { re: /plateau\b/i, name: "Le Plateau (centre)", lat: 5.3266, lng: -4.0173 },
  { re: /gare (de )?bassam|gare sud/i, name: "Gare Sud (Plateau)", lat: 5.3172, lng: -4.0186 },
  { re: /chu de? (cocody|treichville|yopougon)/i, name: "CHU", lat: 5.3441, lng: -3.9905 },
  { re: /universit[ée].*(cocody|houphou[ëe]t)/i, name: "Université FHB (Cocody)", lat: 5.3468, lng: -3.9862 },
  { re: /sococ[ée]/i, name: "Sococé 2 Plateaux", lat: 5.3672, lng: -4.0014 },
  { re: /playce|carrefour marcory/i, name: "PlaYce Marcory", lat: 5.3013, lng: -3.9814 },
  { re: /zoo\b/i, name: "Zoo d'Abidjan", lat: 5.3714, lng: -4.0089 },
  { re: /pont hkb|henri konan/i, name: "Pont HKB", lat: 5.2996, lng: -4.0064 },
];

async function resolveDestination(query) {
  const q = String(query || "").trim();
  if (!q) return null;
  for (const l of LANDMARKS) {
    if (l.re.test(q)) return { name: l.name, lat: l.lat, lng: l.lng, source: "landmark" };
  }
  if (!MAPBOX_TOKEN) return null;
  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&proximity=-4.0083,5.3599&country=CI&limit=1&language=fr`;
    const r = await fetch(url);
    const d = await r.json();
    const f = d?.features?.[0];
    if (f && Array.isArray(f.center)) {
      return { name: f.place_name_fr || f.place_name || q, lat: f.center[1], lng: f.center[0], source: "mapbox" };
    }
  } catch (_) { /* réseau */ }
  return null;
}

export default function GpsNavCard({ query }) {
  const [dest, setDest] = useState(query || "");
  const [place, setPlace] = useState(null);
  const [state, setState] = useState("resolving"); // resolving | ready | notfound
  const [starting, setStarting] = useState(false);

  const doResolve = useCallback(async (q) => {
    setState("resolving");
    const p = await resolveDestination(q);
    if (p) { setPlace(p); setState("ready"); } else { setPlace(null); setState("notfound"); }
  }, []);

  useEffect(() => { doResolve(query); }, [query, doResolve]);

  const confirm = () => {
    if (!place) return;
    setStarting(true);
    const url = `/navigate?dest=${place.lat.toFixed(6)},${place.lng.toFixed(6)}&label=${encodeURIComponent(place.name)}`;
    setTimeout(() => { window.location.href = url; }, 350);
  };

  return (
    <div data-testid="gps-nav-card" style={{
      marginTop: 10, padding: 14, borderRadius: 14, maxWidth: 440,
      background: "linear-gradient(135deg, rgba(0,255,136,0.07), rgba(96,165,250,0.06))",
      border: "1px solid rgba(0,255,136,0.35)",
    }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.6, color: "#00FF88", marginBottom: 8 }}>
        🧭 GPS AYA · PRÊT À DÉMARRER
      </div>
      <label style={{ fontSize: 10.5, opacity: 0.55, display: "block", marginBottom: 4 }}>Destination</label>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          data-testid="gps-nav-dest-input"
          value={dest}
          onChange={(e) => setDest(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") doResolve(dest); }}
          style={{
            flex: 1, padding: "10px 12px", borderRadius: 10, fontSize: 13,
            background: "rgba(0,0,0,0.3)", color: "#F4F1E8",
            border: "1px solid rgba(255,255,255,0.16)", outline: "none",
          }}
        />
        <button
          data-testid="gps-nav-research-btn"
          onClick={() => doResolve(dest)}
          aria-label="Rechercher la destination"
          style={{
            padding: "0 14px", borderRadius: 10, cursor: "pointer",
            background: "rgba(255,255,255,0.08)", color: "#F4F1E8",
            border: "1px solid rgba(255,255,255,0.16)", fontSize: 15,
          }}
        >🔍</button>
      </div>

      {state === "resolving" && (
        <div data-testid="gps-nav-resolving" style={{ marginTop: 10, fontSize: 12, opacity: 0.6 }}>⏳ Je localise la destination…</div>
      )}
      {state === "ready" && place && (
        <div data-testid="gps-nav-resolved" style={{
          marginTop: 10, padding: "9px 12px", borderRadius: 10, fontSize: 12.5,
          background: "rgba(0,255,136,0.08)", border: "1px solid rgba(0,255,136,0.25)",
        }}>
          📍 <strong>{place.name}</strong>
          <span style={{ opacity: 0.5, fontSize: 10.5 }}> · {place.lat.toFixed(4)}, {place.lng.toFixed(4)}</span>
        </div>
      )}
      {state === "notfound" && (
        <div data-testid="gps-nav-notfound" style={{ marginTop: 10, fontSize: 12, color: "#FBBF24" }}>
          🤔 Je n'ai pas trouvé cet endroit — précise le quartier (ex : « pharmacie Riviera 3 ») et relance 🔍
        </div>
      )}

      <button
        data-testid="gps-nav-confirm-btn"
        onClick={confirm}
        disabled={state !== "ready" || starting}
        style={{
          marginTop: 12, width: "100%", padding: "12px 14px", borderRadius: 999,
          border: "none", cursor: state === "ready" ? "pointer" : "not-allowed",
          fontWeight: 800, fontSize: 13.5,
          background: state === "ready" ? "linear-gradient(135deg, #00FF88, #00CC6E)" : "rgba(255,255,255,0.08)",
          color: state === "ready" ? "#052012" : "rgba(255,255,255,0.35)",
          transition: "background 200ms ease",
        }}
      >{starting ? "🚀 Démarrage du GPS…" : "🚀 Confirmer la destination — démarrer le GPS"}</button>
    </div>
  );
}
